import {componentItems} from 'app/componentItems';
import {ShouldSkipLazyLoadType} from 'app/interface';
import {getComponentConfiguration} from 'app/utils/config';
import {hasProp} from 'app/utils/hasProp';
import {filterOutContainerDescendantComponents} from 'app/utils/initialize/filterOutContainerDescendantComponents';
import {getLoadingType} from 'app/utils/initialize/getLoadingType';
import {ComponentConfig} from 'interfaces/foundation/components/componentConfig';

/**
 * Check whether nested components should skip lazy-loading, i.e. because of the deep-linking.
 * To be used by container components like Tab navigation which can hold other components.
 * @param contextElement Context where to search for components
 */
export function shouldSkipLazyLoadNested(contextElement: HTMLElement | DocumentFragment) {
    const notInitialized = Array.from(contextElement.querySelectorAll<HTMLElement>('.gem-module-not-initialized'));
    const componentsWithoutContainerDescendantComponents = filterOutContainerDescendantComponents(notInitialized);
    const webComponents = Array.from(contextElement.querySelectorAll<HTMLElement>('*')).filter(elementIsWebComponent);

    const results = new Set<ShouldSkipLazyLoadType>();
    [...componentsWithoutContainerDescendantComponents, ...webComponents].forEach((element) => {
        const componentNamesAttr = element.dataset.gemModuleName;
        if (!componentNamesAttr && !elementIsWebComponent(element)) {
            results.add('LOAD_LAZY');
            return;
        }

        let config: ComponentConfig | undefined;
        try {
            config = getComponentConfiguration(element);
        } catch {
            // Keep `config` undefined
        }

        if (componentNamesAttr) {
            componentNamesAttr
                .split(',')
                .map((componentName) => componentName.trim())
                .filter((componentName) => componentItems[componentName] !== undefined)
                .forEach((componentName) => {
                    const item = componentItems[componentName];

                    if (item.hasConfig && !config) {
                        console.error(`Config is required for nested component "${componentName}", but was not found`);
                        return;
                    }

                    results.add(item.shouldSkipLazyLoad?.({element, config}) || 'LOAD_LAZY');
                });
        } else if (elementIsWebComponent(element)) {
            const WebComponentConstructor = customElements.get(element.tagName.toLowerCase());
            if (!WebComponentConstructor) {
                return;
            }

            const webComponent = new WebComponentConstructor();
            if (hasProp('shouldSkipLazyLoad', webComponent)) {
                results.add(webComponent.shouldSkipLazyLoad({element, config}) || 'LOAD_LAZY');
            }
        }
    });

    return getLoadingType(results);
}

function elementIsWebComponent(element: HTMLElement) {
    return element.tagName.includes('-');
}
